import { CopyButton } from "~/components/assistant/CopyButton";
import { DetailField, type InstructionsContent } from "~/components/assistant/messages/DetailField";
import { hasContent, unwrapRaw } from "~/components/assistant/messages/toolContent";
import type { ToolCall } from "~/components/assistant/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";

interface ToolContentProps {
  toolCall: ToolCall;
}

interface ParsedArgs {
  instructions: string | null;
  rest: object | null;
}

/**
 * Extract instructions from unwrapped args, returning it separately from remaining fields.
 */
function parseArgs(unwrapped: string | object): ParsedArgs {
  if (
    typeof unwrapped === "object" &&
    unwrapped !== null &&
    !Array.isArray(unwrapped) &&
    "instructions" in unwrapped &&
    typeof (unwrapped as { instructions: unknown }).instructions === "string"
  ) {
    const { instructions, ...rest } = unwrapped as { instructions: string; [key: string]: unknown };
    return {
      instructions,
      rest: Object.keys(rest).length > 0 ? rest : null,
    };
  }
  return { instructions: null, rest: null };
}

export function DefaultToolContent({ toolCall }: ToolContentProps) {
  const hasOutput = hasContent(toolCall.content);

  const unwrappedArgs = unwrapRaw(toolCall.args);
  const { instructions, rest } = parseArgs(unwrappedArgs);

  // If has instructions, show instructions as plain text (with optional rest JSON below)
  // Otherwise show full args as JSON
  const inputContent: string | object | InstructionsContent = instructions
    ? rest
      ? { __kind: "instructions" as const, text: instructions, rest } // instructions + additional args
      : instructions // just instructions as string
    : unwrappedArgs; // no instructions, show full args

  // While running with no output, show input directly without tabs
  if (!hasOutput) {
    return (
      <div className="group relative pt-1">
        <CopyButton
          text={JSON.stringify(toolCall.args, null, 2)}
          variant="light"
          size="sm"
          className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
        />
        <DetailField content={inputContent} />
      </div>
    );
  }

  return (
    <Tabs defaultValue="output" className="gap-0">
      <TabsList variant="underlined" className="mb-2">
        <TabsTrigger value="output" className="text-xs py-1.5 pb-2 mr-4">
          Output
        </TabsTrigger>
        <TabsTrigger value="input" className="text-xs py-1.5 pb-2">
          Input
        </TabsTrigger>
      </TabsList>

      <TabsContent value="output">
        <div className="group relative">
          <CopyButton
            text={JSON.stringify(toolCall.content, null, 2)}
            variant="light"
            size="sm"
            className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          />
          <DetailField content={unwrapRaw(toolCall.content)} />
        </div>
      </TabsContent>

      <TabsContent value="input">
        <div className="group relative">
          <CopyButton
            text={JSON.stringify(toolCall.args, null, 2)}
            variant="light"
            size="sm"
            className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          />
          <DetailField content={inputContent} />
        </div>
      </TabsContent>
    </Tabs>
  );
}
